import React, { useMemo, useState } from "react";

import { HighlightOff as HighlightOffIcon } from "@material-ui/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import debounce from "lodash.debounce";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import Select from "react-select";
import { toast } from "sonner";

import AdminUserAPI from "../../../api/AdminUserAPI";
import default_avatar from "../../../resources/default_avatar.png";
import { getSelectClassNames } from "../../form/Select";

import type { JSX } from "react";
import type { OnChangeValue } from "react-select";

interface ModeratorOption {
  value: string;
  label: string;
}

interface Moderator {
  avatar_url?: string;
  email: string;
  first_name: string;
  inserted_at: string;
  last_name: string;
  storefront_project_uuid: string;
  updated_at: string;
  uuid: string;
}

function ModeratorItem({ moderator }: { moderator: Moderator }): JSX.Element {
  const {
    avatar_url: avatarUrl,
    first_name: firstName,
    last_name: lastName,
    uuid,
  } = moderator;

  const { projectSlug } = useParams<{ projectSlug: string }>();
  const queryClient = useQueryClient();

  const { t } = useTranslation("editor-platform");

  const deleteModeratorMutation = useMutation({
    mutationFn: async ({ userUuid }: { userUuid: string }) =>
      AdminUserAPI.deleteModerator(projectSlug, userUuid),
    onSuccess: (): void => {
      queryClient.invalidateQueries(["getModeratorUserList"]);
      toast.success(t("moderatorDeleteSuccess"));
    },
    onError: (error): void => {
      console.error(error);
      toast.error(t("moderatorDeleteError"));
    },
  });

  const handleDeleteModerator = (): void =>
    deleteModeratorMutation.mutate({ userUuid: uuid });

  return (
    <div className="bg-muted relative m-[0.46875rem] flex h-[2.1875rem] items-center rounded-full pl-[2.5rem]">
      <div
        className="border-border-strong absolute left-0 size-[2.5rem] rounded-full border bg-cover bg-center"
        style={{
          backgroundImage: `url(${avatarUrl ?? default_avatar})`,
        }}
      />
      <div className="px-[0.46875rem]">{`${firstName} ${lastName}`}</div>
      <div className="flex items-center justify-center pr-[0.46875rem]">
        <HighlightOffIcon
          className="hover:text-primary text-subtle cursor-pointer"
          onClick={handleDeleteModerator}
        />
      </div>
    </div>
  );
}

function ModeratorList(): JSX.Element {
  const [search, setSearch] = useState<string>("");

  const { projectSlug } = useParams<{ projectSlug: string }>();
  const queryClient = useQueryClient();

  const { t } = useTranslation("editor-platform");

  const addModeratorMutation = useMutation({
    mutationFn: async ({ uuid }: { uuid: string }) =>
      AdminUserAPI.addModerator(projectSlug, uuid),
    onSuccess: (): void => {
      queryClient.invalidateQueries(["getModeratorUserList"]);
      toast.success(t("moderatorAddSuccess"));
    },
    onError: (error): void => {
      console.error(error);
      toast.error(t("moderatorAddError"));
    },
  });

  const getAdminUserModeratorListQuery = AdminUserAPI.generateQuery({
    fn: AdminUserAPI.getModeratorUserList,
    params: [projectSlug],
  });

  const { data: adminUserModeratorList } = useQuery(
    getAdminUserModeratorListQuery,
  );

  const getAdminUserSearchQuery = AdminUserAPI.generateQuery({
    fn: AdminUserAPI.getUserSearch,
    params: [search, projectSlug],
  });

  const { isLoading, data: adminUserSearch } = useQuery({
    ...getAdminUserSearchQuery,
    enabled: search !== "",
  });

  const handleChangeSearch = useMemo(
    () =>
      debounce((searchText: string) => {
        setSearch(searchText);
      }, 500),
    [],
  );

  const handleChange = (
    selectedOption: OnChangeValue<ModeratorOption, false>,
  ): void => {
    if (selectedOption) {
      addModeratorMutation.mutate({ uuid: selectedOption.value });
    }
    setSearch("");
  };

  const options: ModeratorOption[] = adminUserSearch
    ? adminUserSearch.data?.map((user: Moderator) => ({
        value: user.uuid,
        label: `${user.first_name} ${user.last_name}`,
      }))
    : [];

  const selectClassNames = getSelectClassNames();

  return (
    <div>
      <div className="my-2.5 flex flex-row flex-wrap">
        {adminUserModeratorList?.data.map((moderator: Moderator) => (
          <ModeratorItem key={moderator.uuid} moderator={moderator} />
        ))}
      </div>
      <div className="w-87.5">
        <Select
          closeMenuOnSelect
          classNamePrefix="slct"
          classNames={selectClassNames}
          isLoading={isLoading ? search !== "" : false}
          menuIsOpen={search !== ""}
          noOptionsMessage={(): string => t("noResult")}
          onChange={handleChange}
          onInputChange={handleChangeSearch}
          options={options}
          placeholder={t("searchPlaceholder")}
          value={null}
        />
      </div>
    </div>
  );
}

export default ModeratorList;
